<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import Button from 'primevue/button'
import Popover from 'primevue/popover'
import Select from 'primevue/select'
import SelectButton from 'primevue/selectbutton'
import HeaderStatus from '@/components/layout/HeaderStatus.vue'
import ThemeToggle from '@/components/layout/ThemeToggle.vue'
import BrandMark from '@/components/BrandMark.vue'
import { NAV_SECTIONS } from '@/router'
import { Role, useAuthStore } from '@/stores/auth'
import { setLocale, SUPPORTED_LOCALES, type Locale } from '@/i18n'

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()
const auth = useAuthStore()

/**
 * The menu is built from the route table, filtered by role. Routes marked hidden
 * are reachable by link but do not belong in navigation — a device's detail screen
 * is opened from the device list, not from the menu.
 */
const menuItems = computed(() =>
  router
    .getRoutes()
    .filter((route) => route.meta.titleKey && !route.meta.hidden)
    .filter((route) => {
      const required = route.meta.roles
      if (auth.isDistributorOnly) {
        return required?.includes(Role.DISTRIBUTOR)
      }
      return !required || auth.hasRole(...required)
    })
    .map((route) => ({
      label: t(route.meta.titleKey as string),
      icon: route.meta.icon,
      route: route.path,
      section: route.meta.section ?? '',
    })),
)

/**
 * The menu in sections: the dashboard on its own, then the screens grouped by what
 * they are for. A route names its section in `meta`; a section with nothing the
 * user may open is not drawn.
 */
const menuSections = computed(() =>
  NAV_SECTIONS.map((section) => ({
    section,
    label: section ? t(`nav.sections.${section}`) : '',
    items: menuItems.value.filter((item) => item.section === section),
  })).filter((group) => group.items.length > 0),
)

/** The top bar's trail: the console, then the screen you are on. */
const currentTitle = computed(() => {
  const key = route.matched.at(-1)?.meta.titleKey
  return key ? t(key) : ''
})

const localeOptions = SUPPORTED_LOCALES.map((code) => ({
  label: code.toUpperCase(),
  value: code,
}))

function changeLocale(value: Locale | null) {
  if (value) {
    setLocale(value)
  }
}

// ------------------------------------------------------------ collapse

/**
 * The sidebar folds to an icons-only rail from the top bar's toggle. Remembered
 * per browser, like the interface language: an operator who wants the room for
 * the device table wants it every time.
 */
const COLLAPSED_KEY = 'mdm.sidebarCollapsed'
const remembered = (() => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true'
  } catch {
    return false
  }
})()
/** Tablets and phones start with the rail: the full sidebar would take a third of the screen. */
const NARROW = window.matchMedia('(max-width: 1024px)')
const collapsed = ref(NARROW.matches || remembered)
watch(collapsed, (value) => {
  if (!NARROW.matches) {
    try {
      localStorage.setItem(COLLAPSED_KEY, String(value))
    } catch {
      // Private mode: only this visit keeps it.
    }
  }
})
const onWidth = (e: MediaQueryListEvent) => (collapsed.value = e.matches || remembered)
onMounted(() => NARROW.addEventListener('change', onWidth))
onBeforeUnmount(() => NARROW.removeEventListener('change', onWidth))

// ------------------------------------------------------------- account

const accountMenu = ref<InstanceType<typeof Popover> | null>(null)

const userName = computed(() => auth.displayName ?? auth.username ?? '')
const initial = computed(() => userName.value.slice(0, 1).toUpperCase() || '?')

/**
 * `ROLE_MDM_ORG_ADMIN` → `Org admin`: sentence case, never capitals
 * (docs/UI-GUIDELINES.md §7). Roles outside the MDM set (Keycloak's defaults such
 * as offline_access) are not shown.
 */
const roleLine = computed(() =>
  auth.roles
    .filter((role) => role.startsWith('ROLE_MDM_'))
    .map((role) => role.replace(/^ROLE_MDM_/, '').replace(/_/g, ' ').toLowerCase())
    .map((role) => role.charAt(0).toUpperCase() + role.slice(1))
    .join(', '),
)

// -------------------------------------------------------- organization

/**
 * The organization switcher. Shown only when the token grants more than one organization;
 * with one, its name is simply printed. Switching reloads the console so no screen
 * keeps rows of the organization it left.
 */
const organizationOptions = computed(() =>
  auth.organizations.map((alias) => ({
    label: alias === auth.currentOrganization?.alias ? auth.currentOrganization.name : alias,
    value: alias,
  })),
)

function changeOrganization(alias: string | null) {
  if (alias && alias !== auth.currentOrganization?.alias) {
    auth.switchOrganization(alias)
  }
}

function toggleAccount(event: Event) {
  accountMenu.value?.toggle(event)
}

function openProfile() {
  accountMenu.value?.hide()
  router.push('/profile')
}
</script>

<template>
  <div class="shell" :class="{ 'shell--collapsed': collapsed }">
    <aside class="shell__sidebar">
      <RouterLink to="/dashboard" class="shell__brand" :aria-label="t('nav.dashboard')">
        <BrandMark />
        <span v-if="!collapsed" class="shell__brand-text">
          <span class="shell__wordmark">LiverPro MDM</span>
          <span class="shell__tagline">{{ t('app.subtitle') }}</span>
        </span>
      </RouterLink>

      <nav class="shell__nav">
        <div v-for="group in menuSections" :key="group.section" class="shell__section">
          <div v-if="group.label && !collapsed" class="shell__section-label">{{ group.label }}</div>
          <RouterLink
            v-for="item in group.items"
            :key="item.route"
            v-slot="{ href, navigate, isExactActive }"
            :to="item.route"
            custom
          >
            <a
              :href="href"
              class="shell__nav-item"
              :class="{ 'shell__nav-item--active': isExactActive }"
              :aria-current="isExactActive ? 'page' : undefined"
              :title="collapsed ? item.label : undefined"
              :aria-label="collapsed ? item.label : undefined"
              @click="navigate"
            >
              <span class="shell__nav-icon" :class="item.icon" />
              <span v-if="!collapsed" class="shell__nav-label">{{ item.label }}</span>
            </a>
          </RouterLink>
        </div>
      </nav>

      <!-- The account badge at the foot of the sidebar, as in the SMG admin: it
           opens the menu upward with the profile and the way out. -->
      <button
        type="button"
        class="shell__account"
        :title="collapsed ? userName : undefined"
        aria-haspopup="true"
        @click="toggleAccount"
      >
        <span class="shell__avatar">{{ initial }}</span>
        <template v-if="!collapsed">
          <span class="shell__account-name">{{ userName }}</span>
          <span class="pi pi-chevron-up shell__account-chevron" />
        </template>
      </button>

      <Popover ref="accountMenu" class="account-menu">
        <div class="account-menu__head">
          <span class="shell__avatar shell__avatar--lg">{{ initial }}</span>
          <span class="account-menu__who">
            <span class="account-menu__name">{{ userName }}</span>
            <span v-if="roleLine" class="account-menu__roles">{{ roleLine }}</span>
          </span>
        </div>

        <div class="account-menu__group">
          <button type="button" class="account-menu__item" @click="openProfile">
            <span class="pi pi-user" />{{ t('nav.profile') }}
          </button>
        </div>

        <div class="account-menu__group account-menu__group--last">
          <button type="button" class="account-menu__item" @click="auth.logout()">
            <span class="pi pi-sign-out" />{{ t('nav.logout') }}
          </button>
        </div>
      </Popover>
    </aside>

    <div class="shell__main">
      <header class="shell__topbar">
        <Button
          :icon="collapsed ? 'pi pi-angle-double-right' : 'pi pi-angle-double-left'"
          severity="secondary"
          text
          size="small"
          :aria-label="t('nav.toggleMenu')"
          :title="t('nav.toggleMenu')"
          :aria-pressed="collapsed"
          @click="collapsed = !collapsed"
        />

        <nav class="shell__crumbs" aria-label="breadcrumb">
          <RouterLink to="/dashboard" class="shell__crumb">MDM</RouterLink>
          <template v-if="currentTitle">
            <span class="shell__crumb-sep">/</span>
            <span class="shell__crumb shell__crumb--current">{{ currentTitle }}</span>
          </template>
        </nav>

        <Select
          v-if="organizationOptions.length > 1"
          class="shell__org"
          :model-value="auth.currentOrganization?.alias ?? null"
          :options="organizationOptions"
          option-label="label"
          option-value="value"
          size="small"
          :aria-label="t('nav.organization')"
          @update:model-value="changeOrganization"
        />
        <span v-else-if="auth.currentOrganization" class="shell__org-name">
          <span class="pi pi-building" />{{ auth.currentOrganization.name }}
        </span>

        <HeaderStatus />

        <ThemeToggle />

        <SelectButton
          class="shell__locale"
          :model-value="locale"
          :options="localeOptions"
          option-label="label"
          option-value="value"
          :allow-empty="false"
          size="small"
          @update:model-value="changeLocale"
        />
      </header>

      <main class="shell__content">
        <slot />
      </main>
    </div>
  </div>
</template>

<style scoped>
/* The Console shell (docs/UI-GUIDELINES.md §5): the sidebar and the top bar stay
   put, only the content pane scrolls. Desktop-first by requirement — this panel
   is used at a workstation. */
.shell {
  display: grid;
  grid-template-columns: var(--sidebar-width) minmax(0, 1fr);
  height: 100vh;
  overflow: hidden;
  transition: grid-template-columns var(--duration-normal) var(--ease-standard);
}
.shell--collapsed { grid-template-columns: var(--sidebar-rail-width) minmax(0, 1fr); }

.shell__sidebar {
  display: flex;
  flex-direction: column;
  gap: var(--space-20);
  min-width: 0;
  padding: var(--space-20) var(--space-16);
  background: var(--surface-page);
  border-right: 1px solid var(--border-hairline);
  overflow-x: hidden;
  overflow-y: auto;
}
.shell--collapsed .shell__sidebar { padding: var(--space-20) var(--space-12); }

.shell__brand {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  padding: 0 var(--space-8);
  color: var(--text-primary);
}
.shell__brand:hover { text-decoration: none; }
.shell--collapsed .shell__brand { justify-content: center; padding: 0; }
.shell__brand-text { display: flex; flex-direction: column; min-width: 0; }
.shell__wordmark {
  font: var(--fw-semibold) 15px/20px var(--font-sans);
  letter-spacing: -0.03em;
}
.shell__tagline {
  font: var(--text-micro);
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Sections 20px apart; inside one, rows 2px apart (SMG SidebarNav). */
.shell__nav {
  display: flex;
  flex-direction: column;
  gap: var(--space-20);
}
.shell__section { display: flex; flex-direction: column; gap: var(--space-2); }
.shell__section-label {
  padding: 0 var(--space-8) var(--space-8);
  font: var(--text-body);
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.shell__nav-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-8);
  height: 30px;
  padding: 0 var(--space-8);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  transition: var(--transition-control);
}
.shell--collapsed .shell__nav-item { justify-content: center; padding: 0; }
.shell__nav-item:hover { background: var(--surface-hover); text-decoration: none; }
.shell__nav-item--active { background: var(--surface-selected); }

/* The selected row's 3px ink marker, at the sidebar's inner edge; the rail has
   no room for it and relies on the wash alone. */
.shell__nav-item--active::before {
  content: '';
  position: absolute;
  left: calc(-1 * var(--space-8));
  top: 7px;
  bottom: 7px;
  width: 3px;
  border-radius: var(--radius-pill);
  background: var(--ink-1000);
}
.shell--collapsed .shell__nav-item--active::before { display: none; }

.shell__nav-icon {
  font-size: 16px;
  width: 18px;
  text-align: center;
  color: var(--ink-700);
}

.shell__nav-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Account badge: pushed to the bottom, 32px, the Console hover wash. */
.shell__account {
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: var(--space-8);
  width: 100%;
  height: var(--control-height);
  padding: 0 var(--space-8);
  border: 0;
  border-radius: var(--radius-control);
  background: none;
  cursor: pointer;
  font: var(--text-body-medium);
  color: var(--text-primary);
  text-align: left;
  transition: var(--transition-control);
}
.shell--collapsed .shell__account { justify-content: center; padding: 0; }
.shell__account:hover { background: var(--surface-hover); }
.shell__account:focus-visible { outline: none; box-shadow: var(--ring-focus); }
.shell__account-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.shell__account-chevron { font-size: 12px; color: var(--text-tertiary); }

.shell__avatar {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: var(--radius-avatar);
  background: var(--tint-lavender);
  color: var(--ink-700);
  font: var(--text-small-medium);
}
.shell__avatar--lg { width: 36px; height: 36px; font: var(--text-body-medium); }

.shell__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.shell__topbar {
  flex: 0 0 var(--topbar-height);
  display: flex;
  align-items: center;
  gap: var(--space-8);
  padding: 0 var(--page-gutter) 0 var(--space-16);
  border-bottom: 1px solid var(--border-hairline);
}

.shell__crumbs {
  flex: 1;
  display: flex;
  align-items: center;
  gap: var(--space-8);
  min-width: 0;
  white-space: nowrap;
}
.shell__crumb { color: var(--text-secondary); }
.shell__crumb--current { color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; }
.shell__crumb-sep { color: var(--ink-400); }

.shell__org { min-width: 12rem; }
.shell__org-name {
  display: inline-flex;
  align-items: center;
  gap: var(--space-6);
  color: var(--text-secondary);
  white-space: nowrap;
}

.shell__content {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: var(--page-gutter);
}
</style>

<style>
/*
 * Every page's own header (its title and actions: the first <header> of the page) stays at the top of the content
 * pane while the page scrolls. It reaches over the pane's padding so nothing shows above or beside it, and the
 * negative bottom margin keeps the layout as it was before scrolling. A hairline appears once the page has moved
 * (scroll-driven, where the browser supports it).
 */
.shell__content > * > header:first-child {
  position: sticky;
  top: calc(-1 * var(--page-gutter));
  z-index: 20;
  margin: calc(-1 * var(--page-gutter)) calc(-1 * var(--page-gutter)) calc(-1 * var(--space-12));
  padding: var(--page-gutter) var(--page-gutter) var(--space-12);
  background: var(--surface-page);
}
.shell__content > * > header:first-child { align-items: center; }
/* The page title and its description on one line; the description gives way with an ellipsis, the actions never. */
.shell__content > * > header:first-child > div:first-child:has(> h1 + p) {
  display: flex; flex-direction: row; align-items: baseline; gap: var(--space-12); flex: 1 1 auto; min-width: 0;
}
.shell__content > * > header:first-child > div:first-child:has(> h1 + p) > h1 { flex: none; white-space: nowrap; }
.shell__content > * > header:first-child > div:first-child:has(> h1 + p) > p {
  flex: 1 1 auto; min-width: 0; margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.shell__content > * > header:first-child > :not(:first-child) { flex-shrink: 0; }
@supports (animation-timeline: scroll()) {
  .shell__content > * > header:first-child {
    animation: page-header-lift linear both;
    animation-timeline: scroll(nearest block);
    animation-range: 0 24px;
  }
}
@keyframes page-header-lift {
  to { box-shadow: 0 1px 0 var(--border-hairline), 0 8px 12px -10px rgb(0 0 0 / 0.22); }
}
/*
 * List pages (class "list-page", table with scroll-height="flex"): the page fills the content pane, so its header,
 * filters, the table's column header and the paginator stay in place and only the rows scroll. Too short a screen
 * gets the ordinary page scroll back.
 */
@media (min-height: 560px) {
  .shell__content > .list-page { height: 100%; min-height: 0; }
  .shell__content > .list-page > .p-datatable { flex: 1 1 auto; min-height: 14rem; }
}

/* Leaflet's panes carry z-index 400+: kept inside the map so they never cover the sticky header. */
.shell__content .leaflet-container { isolation: isolate; }

/* The account menu is teleported to <body>, so it cannot be scoped. Console's
   user menu: identity on top, item groups, the way out last (smg AppShell). */
.account-menu.p-popover { width: 240px; }
/* Console popovers hang off their anchor without an arrow. */
.account-menu.p-popover::before,
.account-menu.p-popover::after { display: none; }
.account-menu .p-popover-content { padding: 0; }
.account-menu__head {
  display: flex; align-items: center; gap: var(--space-12);
  padding: var(--space-12); border-bottom: 1px solid var(--border-hairline);
}
.account-menu__who { display: flex; flex-direction: column; min-width: 0; }
.account-menu__name {
  font: var(--text-body-medium); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.account-menu__roles { font: var(--text-small); color: var(--text-secondary); }
.account-menu__group { padding: var(--space-6); }
.account-menu__group--last { border-top: 1px solid var(--border-hairline); }
.account-menu__item {
  display: flex; align-items: center; gap: var(--space-8); width: 100%;
  height: 30px; padding: 0 var(--space-8);
  border: 0; border-radius: var(--radius-control); background: none; cursor: pointer;
  font: var(--text-body); color: var(--text-primary); text-align: left;
  transition: var(--transition-control);
}
.account-menu__item:hover { background: var(--surface-hover); }
.account-menu__item .pi { width: 18px; font-size: 14px; color: var(--ink-700); text-align: center; }
</style>
